import type { ChangeEvent, FC } from 'react';
import { useCallback, useEffect, useState } from 'react';

import type { TextFieldProps } from '@mui/material';
import { IconButton, InputAdornment, TextField } from '@mui/material';

import CloseIcon from 'components/icons/Close';

export type InputSearchFieldProps = TextFieldProps & {
  elevation?: number;
  value?: string;
};

export const InputSearchField: FC<InputSearchFieldProps> = ({ onChange, placeholder, elevation, value, ...props }) => {
  const [search, setSearch] = useState(value ?? '');

  const onSearchChange = useCallback(
    (event: any, value: string) => {
      setSearch(value);

      if (typeof onChange === 'function') {
        onChange({ ...event, target: { ...event.target, value: value } } as ChangeEvent<HTMLInputElement>);
      }
    },
    [onChange]
  );

  useEffect(() => setSearch(value ?? ''), [value]);

  return (
    <TextField
      variant='outlined'
      size='small'
      placeholder={placeholder ?? 'Search'}
      onChange={(event) => onSearchChange(event, event.target.value)}
      InputProps={{
        endAdornment: search.length > 0 && (
          <InputAdornment position='end'>
            <IconButton onClick={(event) => onSearchChange(event, '')} size='small'>
              <CloseIcon fontSize='small' color='default' />
            </IconButton>
          </InputAdornment>
        ),
        sx: (theme) => ({
          backgroundColor: (theme) => theme.palette.colors.steel.A004,
          borderRadius: theme.spacing(0.25),

          color: search.length > 0 ? 'text.primary' : 'text.disabled',

          '& input': {
            height: theme.spacing(2.5)
          },

          '&.Mui-focused': {
            backgroundColor: theme.palette.common.white,
            boxShadow: elevation ? theme.shadows[elevation] : 'none'
          },

          '&:focus-visible': {
            outline: 'none'
          }
        })
      }}
      value={search}
      autoFocus
      fullWidth
      hiddenLabel
      {...props}
    />
  );
};
